import { Link } from "react-router-dom";
import { AlertTriangle, Cpu, ExternalLink, Gamepad2, Heart, RotateCcw, Usb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageShell, GuideHero } from "@/components/layout/PageShell";
import { PYBRICKS_URL, SUPPORT_PYBRICKS_URL } from "@/components/layout/PoweredByPybricks";

const Step = ({ n, title, children, testId }) => (
  <li className="relative grid gap-4 rounded-3xl border bg-card p-6 sm:grid-cols-[64px_1fr] lg:p-8" data-testid={testId}>
    <span className="font-display text-4xl font-black text-primary">{n}</span>
    <div>
      <h3 className="text-xl font-bold sm:text-2xl">{title}</h3>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{children}</div>
    </div>
  </li>
);

const B = ({ children }) => <b className="text-foreground">{children}</b>;
const Ext = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noreferrer" className="font-semibold text-primary underline" data-testid={testId}>{children}</a>
);

const WinUsbSteps = () => (
  <>
    <ol className="list-decimal space-y-1.5 pl-5" data-testid="winusb-device-manager-steps">
      <li>Put the hub into update mode (see step 4), keeping it plugged in.</li>
      <li>Right-click the <B>Start</B> button and open <B>Device Manager</B>.</li>
      <li>Under <B>Other devices</B>, find <B>LEGO Technic Large Hub in DFU Mode</B> (it has a yellow warning sign).</li>
      <li>Right-click it → <B>Update driver</B> → <B>Browse my computer for drivers</B> → <B>Let me pick from a list of available drivers on my computer</B>.</li>
      <li>Choose <B>Universal Serial Bus devices</B> → <B>Next</B>.</li>
      <li>Pick <B>WinUsb Device</B> on both sides → <B>Next</B> → <B>Yes</B> to the warning. It now appears under Universal Serial Bus devices.</li>
    </ol>
    <p className="rounded-xl bg-muted/70 px-4 py-3" data-testid="winusb-zadig-note">
      <B>Setting up lots of laptops?</B> The free tool <Ext href="https://zadig.akeo.ie" testId="zadig-link">Zadig</Ext> does the same job: open it, choose <B>Options → List All Devices</B>, pick <B>LEGO Technic Large Hub in DFU Mode</B>, choose <B>WinUSB</B> and press <B>Install Driver</B>.
    </p>
  </>
);

export default function FirmwareGuide() {
  return (
    <PageShell testId="firmware-guide-page">
      <GuideHero eyebrow="One-time setup · about 5 minutes" title="Install Pybricks firmware" icon={Cpu}>
        LEGO's own firmware can't pair with a game controller. The free, official Pybricks firmware can. You install it once per hub from code.pybricks.com with a USB cable, and you can put LEGO's firmware back whenever you like.
      </GuideHero>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-8 lg:py-16">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Cpu, t: "SPIKE Prime or Robot Inventor hub", d: "Charged battery. Both hub versions work." },
            { icon: Usb, t: "The hub's USB cable", d: "Plugged straight into the computer." },
            { icon: ExternalLink, t: "Chrome or Edge", d: "On Windows, macOS, ChromeOS or Linux." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-2xl border bg-card p-5">
              <Icon className="h-5 w-5 text-primary" />
              <p className="mt-3 font-display font-bold">{t}</p>
              <p className="mt-1 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>

        <ol className="mt-10 space-y-5">
          <Step n="1" title="Windows only, first time: set up the USB driver" testId="firmware-step-1">
            <p><B>Mac, Chromebook or Linux? Skip this step.</B> Windows needs a driver called <B>WinUSB</B> before the browser can reach a hub in update mode. You do this once per computer. School laptops may need someone with admin rights.</p>
            <WinUsbSteps />
            <p>Newer 2026 hubs may show a different name. Pick the device that only appears while the hub is in update mode.</p>
          </Step>
          <Step n="2" title="Open the Pybricks installer" testId="firmware-step-2">
            <p>
              Open <Ext href="https://code.pybricks.com" testId="pybricks-installer-link">code.pybricks.com</Ext> in Chrome or Edge, open the menu and choose <B>Install Pybricks Firmware</B>. Pick <B>SPIKE Prime Hub</B> (or <B>MINDSTORMS Robot Inventor Hub</B>) and accept the licence.
            </p>
          </Step>
          <Step n="3" title="Name your hub" testId="firmware-step-3">
            <p>When it asks for a <B>hub name</B>, use a short label you'll write on a sticker, like <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">HP-R4-07</code> (room 4, robot 7).</p>
          </Step>
          <Step n="4" title="Put the hub into update mode and install" testId="firmware-step-4">
            <p>The installer shows the exact steps for your hub. For most hubs: turn the hub off and unplug it. Press and <B>hold the Bluetooth button</B>, plug in the USB cable, and keep holding until the Bluetooth light flashes red, green and blue. Then let go.</p>
            <p>Press <B>Connect</B>, choose <B>LEGO Technic Large Hub in DFU Mode</B> in the browser pop-up, and wait about a minute for the install to finish. Don't unplug the hub.</p>
            <p>SPIKE Prime hubs bought in 2026 or later have new electronics and a different button combination. Follow the installer's on-screen steps for those.</p>
          </Step>
          <Step n="5" title="Check it in HubPilot" testId="firmware-step-5">
            <p>Unplug the hub and turn it on. Open any project, press <B>Connect hub</B> and pick its name. The Hub panel should say <B>Pybricks firmware</B>.</p>
            <Button asChild variant="outline" className="rounded-full"><Link to="/try" data-testid="firmware-go-programs">Set up a robot</Link></Button>
          </Step>
        </ol>

        <div className="mt-8 rounded-3xl border-2 border-accent/50 bg-[hsl(var(--accent-soft))] p-6 lg:p-8" data-testid="firmware-hubpilot-notice">
          <div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-accent dark:text-primary" /><h3 className="text-xl font-bold">Hub has HubPilot or HubSync firmware?</h3></div>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">
            We no longer offer our own firmware. Hubs running it can't drive from HubPilot until the official Pybricks firmware is installed. Follow the steps above. Your saved projects aren't affected.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          <div className="rounded-3xl border bg-card p-6" data-testid="firmware-updates-card">
            <RotateCcw className="h-5 w-5 text-accent dark:text-primary" />
            <h3 className="mt-3 text-xl font-bold">Updates & going back to LEGO</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Updates are optional. HubPilot tells you when you connect if a hub is too old for Xbox pairing (it needs Pybricks v3.3 or newer).</li>
              <li>To update, repeat steps 2 to 4 (the Windows driver stays installed). The setup stored on the hub is erased, but your saved projects are safe.</li>
              <li>To restore LEGO firmware, connect the hub to the LEGO Education SPIKE app with USB and accept its firmware update.</li>
            </ul>
          </div>
          <div className="rounded-3xl border bg-card p-6" data-testid="controller-firmware-card">
            <Gamepad2 className="h-5 w-5 text-accent dark:text-primary" />
            <h3 className="mt-3 text-xl font-bold">Xbox controller updates</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Controllers need their Bluetooth LE update. Series X|S controllers usually have it already.</li>
              <li>Xbox One controllers with Bluetooth (model 1708) and Elite Series 2 need updating once: plug into a Windows PC and use the <B>Xbox Accessories</B> app, or update from an Xbox console.</li>
              <li>The original 2013 Xbox One controller has no Bluetooth and won't work.</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 rounded-3xl border bg-card p-6 lg:p-8" data-testid="firmware-pybricks-card">
          <div className="flex items-center gap-2"><Heart className="h-5 w-5 text-primary" /><h3 className="text-xl font-bold sm:text-2xl">About Pybricks</h3></div>
          <div className="mt-3 space-y-3 text-sm text-muted-foreground sm:text-base">
            <p>
              <Ext href={PYBRICKS_URL} testId="firmware-pybricks-site-link">Pybricks</Ext> firmware is what lets your hub pair with an Xbox controller. HubPilot is an independent project: it isn't made, sponsored or endorsed by Pybricks, and it doesn't include or distribute any Pybricks firmware.
            </p>
            <p>Pybricks is © The Pybricks Authors. Much of it is open source under the MIT licence, and some parts, including Xbox controller support, are not.</p>
            <p>If HubPilot helps your class, please <Ext href={SUPPORT_PYBRICKS_URL} testId="firmware-support-pybricks-link">support the Pybricks team</Ext>.</p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
